import {
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react';
import noop from 'utils/noop';

type PathError = {
  path: string;
  message?: string;
};

type QueryErrorStateContextProviderProps = {
  hasCustomValidationHandling?: boolean;
};

export type QueryErrorContextState = {
  pathErrors: PathError[];
  pathWarnings: PathError[];
  setPathErrors: Dispatch<SetStateAction<PathError[]>>;
  setPathWarnings: Dispatch<SetStateAction<PathError[]>>;
  getHasErrorFromFieldPath: (fieldPath: string) => boolean;
  addPathWarning: (path: string, message: string) => void;
  removePathWarning: (path: string) => void;
  getHasWarningFromFieldPath: (fieldPath: string) => boolean;
  resetErrorsFromFieldPath: (fieldPath: string) => void;
};

export const defaultQueryErrorContextState = {
  pathErrors: [],
  pathWarnings: [],
  setPathErrors: noop,
  setPathWarnings: noop,
  getHasErrorFromFieldPath: () => false,
  addPathWarning: noop,
  removePathWarning: noop,
  getHasWarningFromFieldPath: () => false,
  resetErrorsFromFieldPath: noop,
};

export const QueryErrorStateContext = createContext<QueryErrorContextState>(
  defaultQueryErrorContextState,
);
export const useQueryErrorStateContext = () => useContext(QueryErrorStateContext);

/**
 *
 * To manually set path errors or warnings from the feature using query builder,
 * wrap the feature in this context and adjust the prop value customValidationHandling accordingly
 */
export const QueryErrorStateContextProvider: FC<
  PropsWithChildren<QueryErrorStateContextProviderProps>
> = ({ children, hasCustomValidationHandling }) => {
  const [pathErrors, setPathErrors] = useState<PathError[]>([]);
  const [pathWarnings, setPathWarnings] = useState<PathError[]>([]);

  const getHasErrorFromFieldPath = useCallback(
    (fieldPath: string) => pathErrors.filter(({ path }) => path === fieldPath).length > 0,
    [pathErrors],
  );

  const getHasWarningFromFieldPath = useCallback(
    (fieldPath: string) => pathWarnings.filter(({ path }) => path === fieldPath).length > 0,
    [pathWarnings],
  );
  const resetErrorsFromFieldPath = useCallback(
    (fieldPath: string) => {
      setPathErrors((errors) => errors.filter(({ path }) => !path.startsWith(fieldPath)));
      setPathWarnings((warnings) => warnings.filter(({ path }) => !path.startsWith(fieldPath)));
    },
    [setPathErrors, setPathWarnings],
  );

  const removePathWarning = useCallback((pathToRemove: string) => {
    setPathWarnings((prev) => prev.filter(({ path }) => path !== pathToRemove));
  }, []);

  const addPathWarning = useCallback((pathToAdd: string, message: string) => {
    setPathWarnings((prev) =>
      !!prev.find(({ path }) => path === pathToAdd)
        ? prev
        : [...prev, { path: pathToAdd, message }],
    );
  }, []);

  const value = {
    pathErrors,
    pathWarnings,
    setPathErrors,
    setPathWarnings,
    getHasErrorFromFieldPath,
    addPathWarning,
    removePathWarning,
    getHasWarningFromFieldPath,
    resetErrorsFromFieldPath,
  };

  // Bypass context provider here if it is implemented elsewhere
  if (hasCustomValidationHandling) {
    return <>{children}</>;
  }

  return (
    <QueryErrorStateContext.Provider value={value}>{children}</QueryErrorStateContext.Provider>
  );
};
